import React from "react";
import { makeStyles } from "@material-ui/core";
import CallEndIcon from "@material-ui/icons/CallEnd";
import CallIcon from "@material-ui/icons/Call";
import { useSelector } from "react-redux";
import { intl } from "../../../../intl";
import { portalTokens as t } from "../../../../theme/portalTokens";
import AvatarView from "../../../Common/AvatarView";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    maxWidth: 420,
    minWidth: 280,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 8,
    fontFamily: t.fontFamily,
    boxSizing: "border-box",
  },
  avatarWrap: {
    position: "relative",
    width: 96,
    height: 96,
    margin: "8px auto 4px",
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    border: `4px solid ${t.kamuBlue}`,
    boxShadow: "0 12px 28px rgba(0,107,214,0.25)",
  },
  pulseRing: {
    position: "absolute",
    inset: -6,
    borderRadius: "50%",
    border: `2px solid ${t.kamuBlue}`,
    opacity: 0.45,
    animation: "$pulse 1.8s ease-out infinite",
    pointerEvents: "none",
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(0.96)", opacity: 0.55 },
    "70%": { transform: "scale(1.12)", opacity: 0 },
    "100%": { transform: "scale(1.12)", opacity: 0 },
  },
  name: {
    fontSize: 18,
    fontWeight: 700,
    color: t.white,
    margin: "12px 0 0",
    fontFamily: t.fontFamily,
  },
  typeLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#60a5fa",
    margin: "6px 0 0",
    fontFamily: t.fontFamily,
    textTransform: "capitalize",
  },
  status: {
    fontSize: 12,
    color: t.slate400,
    margin: "8px 0 0",
    fontFamily: t.fontFamily,
    animation: "$blink 1.4s ease-in-out infinite",
  },
  "@keyframes blink": {
    "0%, 100%": { opacity: 0.55 },
    "50%": { opacity: 1 },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginTop: 24,
    width: "100%",
  },
  declineBtn: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: t.danger,
    color: t.white,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
    "&:hover": { background: "#b91c1c" },
  },
  answerBtn: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: t.emerald,
    color: t.white,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 20px rgba(16,185,129,0.35)",
    "&:hover": { background: "#059669" },
  },
  endBtn: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: t.danger,
    color: t.white,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
    "&:hover": { background: "#b91c1c" },
  },
  actionHint: {
    display: "flex",
    justifyContent: "center",
    gap: 48,
    width: "100%",
    marginTop: 8,
    fontSize: 11,
    color: t.slate500,
    fontFamily: t.fontFamily,
  },
}));

const InComingOutGoingCall = ({ data, type, onReject, onAnswer, onEnd }) => {
  const classes = useStyles();
  const me = useSelector((state) => state.user.me);

  let fromTitle = data?.fromTitle;
  const payload = data?.payload ? data.payload : data;
  const group = payload?.group;

  const peer =
    group?.type === "individual"
      ? (group.participants || []).find((item) => item.id !== me?.id)
      : null;

  const displayName =
    group?.type === "individual"
      ? `${peer?.firstName || ""} ${peer?.lastName || ""}`.trim() || "Kullanıcı"
      : group?.name || "Grup";

  const avatarId =
    group?.type === "individual" ? peer?.id : group?.id;

  const callKind = payload?.screen
    ? intl.formatMessage({
        id: "messaging.screenShareCall",
        defaultMessage: "ekran paylaşımlı arama",
      })
    : payload?.video && group?.type === "individual"
    ? intl.formatMessage({
        id: "messaging.videoCall",
        defaultMessage: "görüntülü arama",
      })
    : payload?.video && group?.type !== "individual"
    ? intl.formatMessage({
        id: "messaging.videoGroupCall",
        defaultMessage: "görüntülü grup arama",
      })
    : group?.type === "individual"
    ? intl.formatMessage({
        id: "messaging.audioCall",
        defaultMessage: "sesli arama",
      })
    : intl.formatMessage({
        id: "messaging.audioGroupCall",
        defaultMessage: "sesli grup arama",
      });

  const directionLabel = intl.formatMessage({
    id: `messaging.${type}`,
    defaultMessage: type === "inComing" ? "Gelen" : "Giden",
  });

  const statusText =
    type === "inComing"
      ? "Gelen arama..."
      : "Aranıyor...";

  const titleExtra =
    type === "inComing" && fromTitle && group?.type !== "individual"
      ? ` — ${fromTitle}`
      : "";

  return (
    <div className={classes.root}>
      <div className={classes.avatarWrap}>
        <span className={classes.pulseRing} />
        <AvatarView
          className={classes.avatar}
          displayName={displayName}
          typeGroup={group?.type !== "individual"}
          id={avatarId}
        />
      </div>

      <p className={classes.name}>
        {displayName}
        {titleExtra}
      </p>
      <p className={classes.typeLabel}>
        {directionLabel} {callKind}
      </p>
      <p className={classes.status}>{statusText}</p>

      <div className={classes.actions}>
        {type === "inComing" ? (
          <>
            <button
              type="button"
              className={classes.declineBtn}
              onClick={onReject}
              aria-label="Reddet"
            >
              <CallEndIcon />
            </button>
            <button
              type="button"
              className={classes.answerBtn}
              onClick={onAnswer}
              aria-label="Cevapla"
            >
              <CallIcon />
            </button>
          </>
        ) : (
          <button
            type="button"
            className={classes.endBtn}
            onClick={onEnd}
            aria-label="Kapat"
          >
            <CallEndIcon />
          </button>
        )}
      </div>
      {type === "inComing" && (
        <div className={classes.actionHint}>
          <span>Reddet</span>
          <span>Cevapla</span>
        </div>
      )}
    </div>
  );
};

export default InComingOutGoingCall;
